import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import { imageTypes } from '@Constants/rx-ui/constants';
import RxThumbnail from '@Services/RxThumbnail';
import ImageBase64 from '@Components/ImageBase64';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { Button } from '@els/els-react--button';
import * as FormatUtils from '@Services/FormatUtils';

import './AlertStructuresButton.scss';

export function spinnerOrImage({ src, formula }) {
  if (!src) {
    return (
      <SimpleSpinner className="alert-structures-button--spinner" spinnerAnimationClassName="alert-structures-button--spinner-animation" />
    );
  }

  let altText;
  if (formula && formula.trim()) {
    altText = `Linear formula of ${formula}`;
  } else {
    altText = 'Linear formula structure';
  }
  return <img src={src} alt={altText} className="alert-structures-button--image" />;
}

export function mapAlertThumbnail(result) {
  const thumbnail = result[imageTypes.ALERT_THUMBNAIL];

  if (!thumbnail) {
    return '';
  }

  const { url } = thumbnail;

  return FormatUtils.formatPath(url);
}

export function useUrl(structureInfo) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    const structure = structureInfo.get('structure');

    const request = RxThumbnail.memoFetAlertThumbnail(structure);
    const fullImageUrl$ = from(request).pipe(map(mapAlertThumbnail));
    const subscription = fullImageUrl$.subscribe(setUrl);

    return () => {
      subscription.unsubscribe();
    };
  }, [structureInfo]);

  return url;
}

export default function AlertStructuresButton({ openStructureDialog, id, structureInfo, formula }) {
  const url = useUrl(structureInfo);

  const onClick = useCallback(() => {
    const structure = structureInfo.get('structure');
    openStructureDialog(id, structure, {
      name: formula,
      isLinearFormula: true,
    });
  }, [openStructureDialog, id, structureInfo, formula]);

  return (
    <Button
      data-e2e="alert-structures-button"
      className="alert-structures-button e2e-alert-structures"
      type="secondary"
      size="small"
      onClick={onClick}
      aria-label="Alert structures">
      <ImageBase64 src={url}>{(args) => spinnerOrImage({ ...args, formula })}</ImageBase64>
    </Button>
  );
}

AlertStructuresButton.propTypes = {
  id: PropTypes.string.isRequired,
  structureInfo: PropTypes.instanceOf(Map).isRequired,
  openStructureDialog: PropTypes.func.isRequired,
};
